
import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/auth';
import { useSubscriptionState } from './useSubscriptionState';
import { useSubscriptionFetching } from './useSubscriptionFetching';

export const useSubscriptionStatus = () => {
  const { user } = useAuth();
  const userIdRef = useRef<string | null>(null);
  const initialFetchDoneRef = useRef(false);
  
  // Get state management from custom hook
  const {
    currentSubscription,
    isSubscribed,
    isLoading,
    setCurrentSubscription,
    setIsSubscribed,
    setIsLoading,
    checkingTimeoutRef,
    isRefreshingRef,
    isMountedRef,
    setupCleanup,
    clearSubscriptionState,
    setIsRetrying,
    setLastChecked
  } = useSubscriptionState();

  // Get fetching logic from custom hook
  const { fetchSubscriptionStatus, cleanupTimeouts } = useSubscriptionFetching({
    setIsLoading,
    setIsSubscribed,
    setCurrentSubscription,
    setLastChecked,
    setIsRetrying,
    isRefreshingRef,
    isMountedRef
  });

  // Comprehensive cleanup function
  const cleanupSubscriptionStatus = useCallback(() => {
    // Clear any pending timeouts
    if (checkingTimeoutRef.current) {
      window.clearTimeout(checkingTimeoutRef.current);
      checkingTimeoutRef.current = null;
    }
    
    // Clear any fetching timeouts
    cleanupTimeouts();
    
    // Reset loading states
    if (isMountedRef.current) {
      setIsLoading(false);
      isRefreshingRef.current = false;
    }
  }, [
    checkingTimeoutRef, 
    cleanupTimeouts, 
    setIsLoading, 
    isRefreshingRef, 
    isMountedRef
  ]);

  // Setup cleanup on component unmount
  useEffect(() => {
    return setupCleanup();
  }, [setupCleanup]);

  // Fetch subscription once when user is available
  useEffect(() => {
    if (!user || !isMountedRef.current || initialFetchDoneRef.current) {
      return;
    }
    
    // Mark that we've done the initial fetch for this user
    if (user.id) {
      userIdRef.current = user.id;
      initialFetchDoneRef.current = true;
      
      // Perform the initial fetch
      console.log('Performing initial subscription fetch for user:', user.id);
      fetchSubscriptionStatus(false);
    }
  }, [user, isMountedRef, fetchSubscriptionStatus]);
  
  // Reset initialFetchDoneRef when user changes
  useEffect(() => {
    // Check if we have a user and if the user ID has changed
    if (user?.id && userIdRef.current && user.id !== userIdRef.current) {
      console.log('User changed, resetting initialization state');
      initialFetchDoneRef.current = false;
      userIdRef.current = user.id;
    } else if (user?.id && !userIdRef.current) {
      // First initialization
      userIdRef.current = user.id;
    }
  }, [user]);

  return {
    isSubscribed,
    currentSubscription,
    refreshSubscription: fetchSubscriptionStatus,
    isLoading,
    cleanupSubscriptionStatus
  };
};
